import React from 'react';
import NameAndDescriptionCell from '@atlassian/jira-admin-pages-common/src/ui/name-and-description-cell/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	actionsDropdownMenuUIEvent,
	deletePrioritySchemeDropdownItemUIEvent,
} from '../../common/constants';
import {
	NAME_COLUMN_KEY,
	PRIORITIES_COLUMN_KEY,
	PROJECTS_COLUMN_KEY,
	ACTIONS_COLUMN_KEY,
	PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX,
	PRIORITY_SCHEME_MODAL_PROJECTS_TAB_INDEX,
} from '../../common/ui/constants';
import { LockedCell } from '../../common/ui/locked-cell';
import type { OnEditSchemeParam, PriorityScheme } from '../../common/ui/types';
import { type DropdownCellProps, DropdownCell } from './dropdown-cell';
import { messages } from './messages';
import { PriorityCell } from './priority-cell';
import { ProjectCell } from './project-cell';

type UsePrioritySchemesTable = {
	isLocked: boolean;
	onEditScheme: (param: OnEditSchemeParam) => void;
	setDeleteModalOpen: (priorityScheme: PriorityScheme) => void;
	prioritySchemes: PriorityScheme[];
};

export const usePrioritySchemesTable = ({
	isLocked,
	onEditScheme,
	setDeleteModalOpen,
	prioritySchemes,
}: UsePrioritySchemesTable) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const head = {
		cells: [
			{
				key: NAME_COLUMN_KEY,
				content: formatMessage(messages.name),
				isSortable: false,
				width: 35,
			},
			{
				key: PRIORITIES_COLUMN_KEY,
				content: formatMessage(messages.priorities),
				isSortable: false,
				width: 30,
			},
			{
				key: PROJECTS_COLUMN_KEY,
				content: formatMessage(messages.projects),
				isSortable: false,
				width: 30,
			},
			{
				key: ACTIONS_COLUMN_KEY,
				content: formatMessage(messages.actions),
				isSortable: false,
				width: 5,
			},
		],
	};

	const rows = prioritySchemes.map((priorityScheme, i) => {
		const { id, name, description, priorities, projects, isDefault } = priorityScheme;

		const deleteMenuItem: DropdownCellProps = {
			title: formatMessage(messages.delete),
			onClick: () => {
				fireUIAnalytics(
					createAnalyticsEvent(deletePrioritySchemeDropdownItemUIEvent),
					deletePrioritySchemeDropdownItemUIEvent.actionSubjectId,
				);
				setDeleteModalOpen(priorityScheme);
			},
		};

		const dropdownCellItems: DropdownCellProps[] = [
			{
				title: formatMessage(messages.edit),
				onClick: () =>
					onEditScheme({
						prioritySchemeId: id,
						startingTab: PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX,
					}),
			},
		];

		if (!isDefault) {
			dropdownCellItems.push(deleteMenuItem);
		}

		const onDropdownMenuToggle = () => {
			const actionsDropdownMenuUIEventWithContext = {
				...actionsDropdownMenuUIEvent,
				containers: {
					priorityScheme: {
						id,
					},
				},
			};

			const actionsDropdownMenuUIEventWithAttributes = {
				prioritySchemeId: id,
				priorities: priorities.map(({ id: priorityId }) => priorityId),
				prioritySchemePriorityCount: priorities.length,
				prioritySchemeHasDescription: Boolean(description),
				prioritySchemeProjects: projects.values.map(({ id: projectId }) => projectId),
				prioritySchemeProjectsCount: projects.total,
			};

			fireUIAnalytics(
				createAnalyticsEvent(actionsDropdownMenuUIEventWithContext),
				actionsDropdownMenuUIEvent.actionSubjectId,
				actionsDropdownMenuUIEventWithAttributes,
			);
		};

		return {
			key: `row-${i}-priority-scheme-${id}`,
			cells: [
				{
					key: NAME_COLUMN_KEY,
					content: (
						<LockedCell columnKey={NAME_COLUMN_KEY} isLocked={isLocked}>
							<NameAndDescriptionCell scheme={{ id, name, description }} />
						</LockedCell>
					),
				},
				{
					key: PRIORITIES_COLUMN_KEY,
					content: (
						<LockedCell columnKey={PRIORITIES_COLUMN_KEY} isLocked={isLocked}>
							<PriorityCell priorities={priorities} />
						</LockedCell>
					),
				},
				{
					key: PROJECTS_COLUMN_KEY,
					content: (
						<LockedCell columnKey={PROJECTS_COLUMN_KEY} isLocked={isLocked}>
							<ProjectCell
								isLocked={isLocked}
								onClick={() =>
									onEditScheme({
										prioritySchemeId: id,
										startingTab: PRIORITY_SCHEME_MODAL_PROJECTS_TAB_INDEX,
									})
								}
								totalCount={projects.total}
								projects={projects.values}
							/>
						</LockedCell>
					),
				},
				{
					key: ACTIONS_COLUMN_KEY,
					content: (
						<LockedCell columnKey={ACTIONS_COLUMN_KEY} isLocked={isLocked}>
							<DropdownCell
								isLocked={isLocked}
								dropdownCellItems={dropdownCellItems}
								onDropdownMenuToggle={onDropdownMenuToggle}
							/>
						</LockedCell>
					),
				},
			],
		};
	});

	return {
		head,
		rows,
	};
};
